import { Mail, MessageCircle } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../components/Button';
import { PageHeader } from '../components/PageHeader';

export function ContactUsPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  })

  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      toast.error('Please fill in all required fields')
      return
    }

    setIsSubmitting(true)
    
    // Simulate form submission - in a real app, you'd send this to a backend
    try {
      // Create mailto link with form data
      const mailtoLink = `mailto:connect@iqueuelab.com?subject=${encodeURIComponent(formData.subject)}&body=${encodeURIComponent(
        `Name: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\n\nMessage:\n${formData.message}`
      )}`
      
      window.location.href = mailtoLink
      
      // Reset form after short delay
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
        })
        toast.success('Email client opened. Please complete and send the email.')
        setIsSubmitting(false)
      }, 500)
    } catch (error) {
      toast.error('Failed to open email client')
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <PageHeader title="Contact Us" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
        {/* Contact Info Cards */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-saffron/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Mail className="w-6 h-6 text-saffron" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Email</h3>
              <p className="text-gray-600 text-sm mb-3">
                Reach out to us via email
              </p>
              <a
                href="mailto:connect@iqueuelab.com"
                className="text-saffron hover:text-orange-600 font-medium text-sm"
              >
                connect@iqueuelab.com
              </a>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-saffron/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <MessageCircle className="w-6 h-6 text-saffron" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">WhatsApp</h3>
              <p className="text-gray-600 text-sm mb-3">
                Chat with us on WhatsApp
              </p>
              <a
                href="https://wa.me/917620769511"
                target="_blank"
                rel="noopener noreferrer"
                className="text-saffron hover:text-orange-600 font-medium text-sm"
              >
                +91 7620769511
              </a>
            </div>
          </div>
        </div>

        {/* <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-saffron/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Phone className="w-6 h-6 text-saffron" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Phone</h3>
              <p className="text-gray-600 text-sm mb-3">Call us directly</p>
              <a href="tel:+917620769511" className="text-saffron hover:text-orange-600 font-medium text-sm">
                +91 7620769511
              </a>
            </div>
          </div>
        </div> */}
      </div>

      {/* <div> */}
      {/* Information Section */}
      <div className="space-y-6 lg:grid lg:grid-cols-2 gap-6">
        {/* Quick Info */}
        <div className="bg-gradient-to-br from-saffron/10 to-orange-50 rounded-lg border border-saffron p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Response Time
          </h3>
          <p className="text-gray-700 text-sm leading-relaxed">
            Our support team typically responds within{" "}
            <span className="font-semibold">24 business hours</span>. For urgent
            matters, please reach out via WhatsApp.
          </p>
        </div>

        {/* Support Hours */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Support Availability
          </h3>
          <div className="space-y-3 text-sm text-gray-700">
            <div className="flex justify-between">
              <span>Monday - Friday</span>
              <span className="font-medium">9:00 AM - 6:00 PM</span>
            </div>
            <div className="flex justify-between">
              <span>Saturday</span>
              <span className="font-medium">10:00 AM - 4:00 PM</span>
            </div>
            <div className="flex justify-between">
              <span>Sunday</span>
              <span className="font-medium">Closed</span>
            </div>
          </div>
        </div>

        {/* What we support */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            We Help With
          </h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <span className="text-saffron font-bold">•</span>
              <span>Account setup and configuration</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-saffron font-bold">•</span>
              <span>Feature usage and troubleshooting</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-saffron font-bold">•</span>
              <span>Data import and export assistance</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-saffron font-bold">•</span>
              <span>Technical support and bug reports</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-saffron font-bold">•</span>
              <span>General inquiries and feedback</span>
            </li>
          </ul>
        </div>

        {/* Quick Contact */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Quick Contact
          </h3>
          <div className="flex flex-col gap-3">
            <Button
              onClick={() =>
                window.open("https://wa.me/917620769511", "_blank")
              }
              className="w-full bg-saffron hover:bg-orange-600"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp Chat
            </Button>
          </div>
        </div>
      </div>
      {/* </div> */}
    </>
  );
}
